// httpPorts — the Conversation BC slice: transcript lifecycle, the compose
// turn, interrupt/opening-move, and the durable grounding pair + its chip read.

import type * as Core from "@bilda/core";
import { Result } from "@bilda/utils";
import type { SituationProfileChipView } from "../../components/Chat/types.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type { ChatGoalsRead } from "../AppShellHost/rightPanel/goal.js";
import type { AuthorLocation } from "../authorLocation.js";
import {
    toChatEntries,
    toReply,
    toTurnStreamEvent,
} from "../conversationAdapter.js";
import {
    type ExternalError,
    mkAgentAuthMissing,
    mkAgentBusy,
    mkAgentFailed,
    mkAgentUnreachable,
    mkDailyCeilingReached,
    mkMalformed,
    mkQuotaExhausted,
    mkUnreachable,
    mkUsageLimitReached,
} from "../externalError.js";
import { parseChatGoals } from "../goalAdapter.js";
import { openTurnStream, TURN_REQUEST_TIMEOUT_MS } from "../httpClient.js";
import type {
    ConversationPorts,
    ConversationTranscript,
} from "../ports/conversation.js";
import type { PortResult } from "../ports/result.js";
import type { MaterialId, OpeningMoveResult, TurnResult } from "../ports.js";
import type { TurnStreamListener } from "../turnStream.js";
import { bool, fields, list, str } from "../wireShape.js";
import { anchorWire, command } from "./shared.js";

// F63 — a conversation's DISPLAY title is derived server-side from its first
// user message (`recentConversations`). At create time there is no message yet,
// so the summary title is EMPTY and every render site shows a neutral localized
// fallback (`t`New conversation`` → "Ny konversation"), never this string. The domain still requires
// a non-empty title on `startConversation` (the `TitleEmpty` guard), so we send
// this stable internal placeholder that is never shown to the author.
// TODO(F63): agent-authored titles could replace the first-message excerpt.
const NASCENT_CONVERSATION_TITLE = "New conversation";
const listConversations = async () => {
    const rList = await command("listConversations", {});
    if (Result.isErr(rList)) return rList;
    return Result.ok(
        list(fields(rList.value)?.get("items")).flatMap((raw) => {
            const f = fields(raw);
            const id = str(f?.get("id"));
            if (id === undefined) return [];
            // `lastActivityAt` — the server-folded last-touched instant
            // (`recentConversations`), falling back to the thread's genesis
            // instant, so every conversation carries one (J33). Kept optional
            // to degrade honestly if a malformed log ever omits it.
            const lastActivityAt = str(f?.get("lastActivityAt"));
            const projectId = str(f?.get("projectId"));
            return [
                {
                    id,
                    // F63 — the server-derived first-message excerpt, or "" for
                    // a thread with no user message yet. Empty (never the id):
                    // each render site maps "" to a neutral localized fallback.
                    title: str(f?.get("title")) ?? "",
                    archived: bool(f?.get("archived")),
                    ...(lastActivityAt === undefined ? {} : { lastActivityAt }),
                    ...(projectId === undefined ? {} : { projectId }),
                },
            ];
        }),
    );
};
const startConversation = async (
    title = NASCENT_CONVERSATION_TITLE,
    conversationId?: string,
    projectId?: string,
) => {
    const rStart = await command("startConversation", {
        title,
        // Only send the field when the caller minted an id — omit it and
        // the server mints, preserving legacy behaviour.
        ...(conversationId === undefined ? {} : { conversationId }),
        ...(projectId === undefined ? {} : { projectId }),
    });
    if (Result.isErr(rStart)) return rStart;
    const created = str(fields(rStart.value)?.get("conversationId"));
    if (created === undefined) {
        return Result.err(
            mkMalformed("startConversation returned no conversationId"),
        );
    }
    // F63 — the optimistic summary's DISPLAY title is EMPTY: a nascent thread
    // has no first user message yet, so its derived title is empty and the rail/
    // tab render the localized fallback immediately (never the internal
    // placeholder above). The server replaces this with the real excerpt on the
    // next `listConversations` once the author sends their first message.
    return Result.ok({
        id: created,
        title: "",
        ...(projectId === undefined ? {} : { projectId }),
    });
};

const renameConversation = async (
    conversationId: string,
    title: string,
): PortResult<void> => {
    const r = await command("renameConversation", {
        conversationId,
        title,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const deleteConversation = async (conversationId: string): PortResult<void> => {
    const r = await command("deleteConversation", { conversationId });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const restoreConversation = async (
    conversationId: string,
): PortResult<void> => {
    const r = await command("restoreConversation", { conversationId });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const archiveConversation = async (
    conversationId: string,
): PortResult<void> => {
    const r = await command("archiveConversation", { conversationId });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const unarchiveConversation = async (
    conversationId: string,
): PortResult<void> => {
    const r = await command("unarchiveConversation", { conversationId });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// The chip's write pair + read (Slice 1). `materialId` is accepted for port
// symmetry with `sendTurn`/`loadConversation` but IGNORED here — same as
// those two, the real server has one ambient conversation per session
// (`ensureConversation`), not one per material.

const groundConversation = async (
    conversationId: string,
    contextId: string,
): PortResult<void> => {
    const r = await command("groundConversation", {
        conversationId,
        contextId,
    });
    if (Result.isErr(r)) return r;
    return Result.ok(undefined);
};

const ungroundConversation = async (
    conversationId: string,
): PortResult<void> => {
    const r = await command("ungroundConversation", {
        conversationId,
    });
    if (Result.isErr(r)) return r;
    return Result.ok(undefined);
};

// Answer the ElicitationForm (LANE S3): dispatch `supply_information`. The
// domain `ElicitationAnswer` is plain JSON at runtime (its branded `FieldKey`s
// are strings), so it rides the command body as-is; the server route re-parses
// it into the domain shape. INTEGRATOR NOTE: the matching server route
// (`server/routes/commands.ts` + the `appContract`/App workflow in @bilda/app)
// is NOT yet present — until it lands this returns a typed transport error
// rather than a silent success.
const supplyInformation = async (
    conversationId: string,
    requestMessageId: string,
    answer: Core.Conversation.Elicitation.ElicitationAnswer,
): PortResult<void> => {
    const r = await command("supplyInformation", {
        conversationId,
        requestMessageId,
        answer,
    });
    if (Result.isErr(r)) return r;
    return Result.ok(undefined);
};

// Wire → the chip's display shape. Cast-free (`wireShape` helpers): a
// malformed/unexpected payload degrades to `no_situation_profile` rather than
// throwing — the chip treats "couldn't read a profile" the same as "there
// isn't one".
const parseSituationProfileView = (
    value: unknown,
): SituationProfileChipView => {
    const f = fields(value);
    if (str(f?.get("type")) === "situation_profile") {
        const contextId = str(f?.get("contextId"));
        const title = str(f?.get("title"));
        const descriptor = str(f?.get("descriptor"));
        if (
            contextId !== undefined &&
            title !== undefined &&
            descriptor !== undefined
        ) {
            return { type: "situation_profile", contextId, title, descriptor };
        }
    }
    return { type: "no_situation_profile" };
};

// The chat's goals, parsed at the boundary (`goalAdapter`).
const conversationGoals = async (
    conversationId: string,
    materialIds: readonly string[],
): PortResult<ChatGoalsRead> => {
    const r = await command("conversationGoals", {
        conversationId,
        materialIds,
    });
    return Result.isErr(r) ? r : Result.ok(parseChatGoals(r.value));
};

const addConversationGoal = async (
    conversationId: string,
    contextId: string,
): PortResult<void> => {
    const r = await command("addConversationGoal", {
        conversationId,
        contextId,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const removeConversationGoal = async (
    conversationId: string,
    contextId: string,
): PortResult<void> => {
    const r = await command("removeConversationGoal", {
        conversationId,
        contextId,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const situationProfileView = async (
    conversationId: string,
): PortResult<SituationProfileChipView> => {
    const r = await command("situationProfileView", {
        conversationId,
    });
    if (Result.isErr(r)) return r;
    return Result.ok(parseSituationProfileView(r.value));
};

// The `materialId` is IGNORED (see the header): reads come from the session's
// ambient conversation. An empty transcript is a valid `ok` state (no
// conversation yet), never an error.

const loadConversation = async (
    conversationId: string,
): PortResult<ConversationTranscript> => {
    const rShow = await command("showConversation", { id: conversationId });
    if (Result.isErr(rShow)) return rShow;
    return Result.ok({
        entries: toChatEntries(rShow.value),
        turnRunning: bool(fields(rShow.value)?.get("turnRunning")),
    });
};

// Get-or-create the ambient conversation, run the turn, adapt the closing
// outcome into the agent reply. `TurnResult.landed` is left ABSENT — the real
// workflow has no landed-proposal to return (the agent's edits are tool
// side-effects in the event log, not a keyed proposal). See the report.

// An `agent_errored` close arrives INSIDE an ok envelope (the workflow
// commits the error note and reports the outcome as data). Lift it into
// the shell's typed failure vocabulary so the host's ONE err channel
// notices it — the transcript never gets a synthesised reply for it.
// `reason.category` is the core's coarse bucket: `permission_denied` is
// minted only from the adapter's agent_auth_missing (no/invalid API key
// on the server — the BYOK case); `quota_exhausted` is the vendor account
// out of credits (valid key, add credits — the billing-400 incident);
// `usage_limit_reached` is the organisation's own spend limit (lifts by
// itself; `detail` is the date it lifts, parsed, never shown raw);
// `daily_ceiling_reached` is our own daily cost ceiling (`detail` names
// which, its limit and reset, parsed at the boundary);
// `provider_busy` is a rate limit / overload (wait a minute); `network` /
// `timeout` mean the server couldn't reach the provider; everything else
// is a generic agent failure carrying the category as telemetry only.
// The provider's own message never reaches this boundary.
export const turnFailureOf = (value: unknown): ExternalError | undefined => {
    const outcome = fields(fields(value)?.get("outcome"));
    if (str(outcome?.get("type")) !== "agent_errored") return undefined;
    const reason = fields(outcome?.get("reason"));
    const category = str(reason?.get("category"));
    if (category === "permission_denied") return mkAgentAuthMissing();
    if (category === "quota_exhausted") return mkQuotaExhausted();
    if (category === "usage_limit_reached")
        return mkUsageLimitReached(reason?.get("detail"));
    if (category === "daily_ceiling_reached")
        return mkDailyCeilingReached(reason?.get("detail"));
    if (category === "provider_busy") return mkAgentBusy();
    if (category === "network" || category === "timeout")
        return mkAgentUnreachable(`agent_errored: ${category}`);
    return mkAgentFailed(`agent_errored: ${category ?? "unknown"}`);
};

// Open the SSE stream for this conversation and relay each incoming
// envelope through `toTurnStreamEvent` into `onEvent`; a message this
// codec doesn't recognise (or can't parse) is silently skipped — this is
// a best-effort LIVE-progress channel, never the turn's authoritative
// outcome. Returns a closer the caller runs once the matching POST
// settles (ok or err) — the CLIENT owns the connection's lifecycle (see
// httpClient's `openTurnStream` header note). No-ops entirely when
// `onEvent` is absent, so a caller that doesn't ask for streaming opens
// no extra connection.
const withTurnStream = (
    conversationId: string,
    onEvent: TurnStreamListener | undefined,
): (() => void) => {
    if (onEvent === undefined) return () => {};
    const source = openTurnStream(conversationId);
    source.onmessage = (message) => {
        let raw: unknown;
        try {
            raw = JSON.parse(message.data);
        } catch {
            return;
        }
        const event = toTurnStreamEvent(raw);
        if (event !== undefined) onEvent(event);
    };
    return () => source.close();
};

// Follow a turn this page did not start (#100) over the same SSE stream a
// sending page opens. Resolves once the server says no turn runs (sent on
// subscribe when none does, and when one ends) or asks for a resync (live
// progress had a gap); the browser reconnects a dropped stream by itself,
// and a reconnect after a server restart hears "none runs" too. Only a
// stream the browser gave up on is an error.
const followTurn = (
    conversationId: string,
    onEvent: TurnStreamListener,
): PortResult<void> =>
    new Promise((resolve) => {
        const source = openTurnStream(conversationId);
        source.onmessage = (message) => {
            let raw: unknown;
            try {
                raw = JSON.parse(message.data);
            } catch {
                return;
            }
            // `resync`: live progress had a gap (scale-out PR 5). Ending the
            // follow re-reads the transcript from the log, which follows on
            // if the turn still runs.
            const type = str(fields(raw)?.get("type"));
            if (type === "no_turn_running" || type === "resync") {
                source.close();
                resolve(Result.ok(undefined));
                return;
            }
            const event = toTurnStreamEvent(raw);
            if (event !== undefined) onEvent(event);
        };
        source.onerror = () => {
            if (source.readyState !== EventSource.CLOSED) return;
            resolve(Result.err(mkUnreachable("turn stream closed")));
        };
    });

const sendTurn = async (
    conversationId: string,
    _materialId: MaterialId | null,
    text: string,
    mentions: readonly ComposerResourceRef[],
    anchor: Core.EntityRef.Ref | null,
    onEvent: TurnStreamListener | undefined,
    resumesRequestId: string | undefined,
    projectId: string | null,
    location: AuthorLocation | undefined,
): PortResult<TurnResult> => {
    const closeStream = withTurnStream(conversationId, onEvent);
    try {
        const wire = anchor === null ? null : anchorWire(anchor);
        // D4: the @-REFERENCE relation. The position-less references (attached
        // files) ride the durable per-message `resourceRefs` channel — ids
        // only; inline @-mentions ride the text and the server adds them (D8).
        // The server
        // resolves each and builds inspect/act POINTERS into the turn prompt
        // (`buildMentionedResourcesPrompt`), distinct from the grounding block
        // the anchor's Context stream feeds. Omit when empty (matches every
        // other optional wire field here).
        const resourceRefs = mentions.map((m) => m.id);
        const r = await command(
            "sendTurn",
            {
                conversationId,
                userText: text,
                // No grounding field: the conversation's durable `grounding`
                // (bound via groundConversation/ungroundConversation) is what
                // the agent's system prompt reads now — sendTurn no longer
                // takes it per-turn. The ACTIVE TAB's anchor rides too (context
                // spine step 3a): the turn reads that anchor's Context stream
                // server-side. Both are omitted when absent.
                ...(resourceRefs.length > 0 ? { resourceRefs } : {}),
                ...(wire !== null
                    ? { anchorKind: wire.kind, anchorId: wire.id }
                    : {}),
                ...(resumesRequestId !== undefined
                    ? { resumesRequestMessageId: resumesRequestId }
                    : {}),
                // The project the author is in: the server parses it and
                // puts that project's brief in this turn's system context.
                ...(projectId !== null ? { projectId } : {}),
                // Where the author is (principle 22): the open page, the
                // caret's block, the selection, the scope chip.
                ...(location === undefined ? {} : { location }),
            },
            // The server holds this POST open for the entire multi-round turn,
            // so it needs the long turn ceiling, not the small-envelope default.
            TURN_REQUEST_TIMEOUT_MS,
        );
        if (Result.isErr(r)) return r;
        const failure = turnFailureOf(r.value);
        if (failure !== undefined) return Result.err(failure);
        return Result.ok({ reply: toReply(r.value) });
    } finally {
        closeStream();
    }
};

// Ask the domain to interrupt the ambient conversation's in-flight turn
// (`POST /commands/interruptTurn`). Fire-and-forget on the domain side —
// `sendTurn`'s between-rounds check honours it at the next boundary — so
// `ok` here means "requested," not "stopped instantly."
const interruptTurn = async (conversationId: string): PortResult<void> => {
    const r = await command("interruptTurn", { conversationId });
    if (Result.isErr(r)) return r;
    return Result.ok(undefined);
};

// PS-61: a message sent while the assistant works waits for its next step.
const sendWaitingMessage = async (
    conversationId: string,
    text: string,
    location: AuthorLocation | undefined,
): PortResult<{ readonly messageId: string }> => {
    const r = await command("sendWaitingMessage", {
        conversationId,
        text,
        ...(location === undefined ? {} : { location }),
    });
    if (Result.isErr(r)) return r;
    const messageId = str(fields(r.value)?.get("messageId"));
    return messageId === undefined
        ? Result.err(mkMalformed("sendWaitingMessage: no messageId"))
        : Result.ok({ messageId });
};

// Edit / Delete / Undo / Send now on waiting messages: one command each.
const waitingAction =
    (name: string) =>
    async (
        conversationId: string,
        messageIds: readonly [string, ...string[]],
    ): PortResult<void> => {
        const r = await command(name, { conversationId, messageIds });
        return Result.isErr(r) ? r : Result.ok(undefined);
    };

// The EXPLICIT-START bridge (onboarding-activation.md §5): fired only on the
// author's accept. Get-or-create the ambient conversation, ask the workflow to
// make the opening move, and adapt the nested turn's close into the agent
// reply. `opening_move_not_offered` — the server's own predicate declined — is
// a first-class ok outcome, not an error.
const offerOpeningMove = async (
    conversationId: string,
): PortResult<OpeningMoveResult> => {
    const r = await command(
        "offerOpeningMove",
        {
            conversationId,
            // Same grounding-from-the-fold read as `sendTurn` — no field here.
        },
        // Runs a full turn server-side (same as sendTurn) — long turn ceiling.
        TURN_REQUEST_TIMEOUT_MS,
    );
    if (Result.isErr(r)) return r;
    if (str(fields(r.value)?.get("type")) !== "opening_move_offered") {
        return Result.ok({ type: "not_offered" });
    }
    // The offered outcome nests a `SendTurnOutput` under `turn`; an errored
    // close rides inside the ok envelope — lift it to the shell's typed failure.
    const turn = fields(r.value)?.get("turn");
    const failure = turnFailureOf(turn);
    if (failure !== undefined) return Result.err(failure);
    return Result.ok({ type: "offered", reply: toReply(turn) });
};

export const conversationHttp: ConversationPorts = {
    // The server's conversations are workspace-global aggregates (no
    // per-material query) — the chip must not claim a narrower scope.
    conversationScope: "workspace",
    listConversations,
    startConversation,
    renameConversation,
    deleteConversation,
    restoreConversation,
    archiveConversation,
    unarchiveConversation,
    loadConversation,
    followTurn,
    sendTurn,
    interruptTurn,
    sendWaitingMessage,
    takeBackWaitingMessages: waitingAction("takeBackWaitingMessages"),
    deleteWaitingMessages: waitingAction("deleteWaitingMessages"),
    restoreWaitingMessages: waitingAction("restoreWaitingMessages"),
    sendWaitingMessagesNow: waitingAction("sendWaitingMessagesNow"),
    offerOpeningMove,
    groundConversation,
    ungroundConversation,
    supplyInformation,
    situationProfileView,
    conversationGoals,
    addConversationGoal,
    removeConversationGoal,
};
